import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, AlertCircle, Loader2, Sun, Moon, Calendar, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  validateName,
  validateZip,
  validateUrgency,
  validateHours,
  validatePayer,
  validateEmail,
  validatePhone,
  type ValidationResult,
} from "@/lib/chat-validation";

type Step =
  | "greeting" | "name" | "zip" | "needs" | "urgency" | "hours" | "payer"
  | "offer_booking" | "confirm_name" | "confirm_email" | "confirm_phone"
  | "select_slot" | "booking_confirmed"
  | "closing" | "done"
  | "retry_name" | "retry_zip" | "retry_urgency" | "retry_hours" | "retry_payer";

interface ChatMsg {
  id: string;
  role: "assistant" | "user" | "error";
  content: string;
  field?: string; // e.g. "ZIP code", "Urgency"
}

interface Slot { start: string; end: string; label: string }

export default function ChatWidget() {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  // Widget-scoped theme. Persisted in localStorage so the user's choice
  // survives reloads. Defaults to the host OS preference on first load.
  // We apply the `dark` class to a local wrapper (not <html>) so the
  // toggle never bleeds into the host page when the widget is embedded.
  const THEME_STORAGE_KEY = "lg-chat-theme";
  const [theme, setTheme] = useState<"light" | "dark">(() => {
    if (typeof window === "undefined") return "light";
    const saved = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (saved === "light" || saved === "dark") return saved;
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    }
  }, [theme]);

  const toggleTheme = () => setTheme(t => (t === "dark" ? "light" : "dark"));

  const [step, setStep] = useState<Step>("greeting");
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState("");
  // Maps each prompt step to the matching client-side validator, so the
  // input field can show inline feedback as the user types — before they
  // hit Send. Steps without an entry (e.g. "needs", "greeting", "done")
  // are free-form and skip inline validation.
  const VALIDATOR_BY_STEP: Partial<Record<Step, (v: string) => ValidationResult>> = {
    name: validateName,
    retry_name: validateName,
    zip: validateZip,
    retry_zip: validateZip,
    urgency: validateUrgency,
    retry_urgency: validateUrgency,
    hours: validateHours,
    retry_hours: validateHours,
    payer: validatePayer,
    retry_payer: validatePayer,
    confirm_name: validateName,
    confirm_email: validateEmail,
    confirm_phone: validatePhone,
  };
  const activeValidator = VALIDATOR_BY_STEP[step];
  // Only surface an inline error once the user has actually typed something
  // — an empty field on first focus shouldn't read as "invalid".
  const inlineError: string | null = (() => {
    if (!activeValidator) return null;
    if (!input.trim()) return null;
    const r = activeValidator(input);
    return r.ok ? null : r.message;
  })();
  const [isTyping, setIsTyping] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [collected, setCollected] = useState<Record<string, any>>({});
  const [leadId, setLeadId] = useState<string | null>(null);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [bookingSlot, setBookingSlot] = useState<string | null>(null);
  const [confirmedSlot, setConfirmedSlot] = useState<Slot | null>(null);
  const [wasIdempotent, setWasIdempotent] = useState(false);
  const [retryStatus, setRetryStatus] = useState<{ attempt: number; max: number } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Holds a timeout + cancel flag for the in-flight submit's backoff sleep so
  // the user can abort retries instantly.
  const retryAbortRef = useRef<{ cancelled: boolean; timeoutId: number | null }>({ cancelled: false, timeoutId: null });
  // Stable idempotency key for this chat session. All retries of submitLead
  // (including server-side validation re-tries) reuse the same key so the
  // backend treats them as a single submission.
  const idempotencyKeyRef = useRef<string>(
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? `chat-${crypto.randomUUID()}`
      : `chat-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  );

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        addBot("Hi there, I'm Sarah! I'll ask a few quick questions to match you with the right care options — I'm not able to answer general questions, but a real person on our team will follow up on anything else. 💙 Let's start: could you tell me the name of the person you're looking for care for?");
      }, 800 + Math.random() * 1500);
    }
  }, [isOpen]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, isTyping]);

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen, messages]);

  function addBot(content: string) {
    setMessages(prev => [...prev, { id: crypto.randomUUID(), role: "assistant", content }]);
  }

  function addUser(content: string) {
    setMessages(prev => [...prev, { id: crypto.randomUUID(), role: "user", content }]);
  }

  function addError(field: string, content: string) {
    setMessages(prev => [...prev, { id: crypto.randomUUID(), role: "error", content, field }]);
  }

  function handleSend() {
    const text = input.trim();
    if (!text || isSubmitting || isTyping || step === "done") return;
    // Inline validation already blocks the Send button when invalid, but we
    // re-check here so an Enter-key press can never bypass the rule.
    if (inlineError) return;
    setInput("");
    addUser(text);
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      processStep(text);
    }, 800 + Math.random() * 1500);
  }

  async function processStep(text: string) {
    switch (step) {
      case "greeting":
      case "name": {
        const r = validateName(text);
        if (!r.ok) { addError("Name", r.message); return; }
        setCollected(p => ({ ...p, name: r.value }));
        addBot(`Thank you for telling me about ${r.value.split(/[,. ]/)[0]}. What's the 5-digit ZIP code where care would be needed?`);
        setStep("zip");
        break;
      }
      case "zip": {
        const r = validateZip(text);
        if (!r.ok) { addError("ZIP code", r.message); return; }
        setCollected(p => ({ ...p, zip_code: r.value }));
        addBot("Could you describe what kind of help is needed? For example: bathing, meals, medication reminders, companionship, etc.");
        setStep("needs");
        break;
      }
      case "needs":
        setCollected(p => ({ ...p, acuity_notes: text }));
        addBot("On a scale of 1 to 5, how urgent is your need? (1 = just exploring, 5 = need care immediately)");
        setStep("urgency");
        break;
      case "urgency": {
        const r = validateUrgency(text);
        if (!r.ok) { addError("Urgency", r.message); return; }
        setCollected(p => ({ ...p, urgency: r.value }));
        addBot("Roughly how many hours per week of care are you thinking? (Any number from 0 to 168.)");
        setStep("hours");
        break;
      }
      case "hours": {
        const r = validateHours(text);
        if (!r.ok) { addError("Hours per week", r.message); return; }
        setCollected(p => ({ ...p, hours_needed: r.value }));
        addBot("Last question — how are you planning to pay for care? (Private Pay, LTC Insurance, Medicaid, Medicare, VA, or Other)");
        setStep("payer");
        break;
      }
      case "payer": {
        const r = validatePayer(text);
        if (!r.ok) { addError("Payer source", r.message); return; }
        setCollected(p => ({ ...p, payer_source: r.value }));
        await submitLead({ ...collected, payer_source: r.value }, r.value);
        break;
      }
      case "offer_booking": {
        if (/^(yes|sure|ok|yep|y|please)/i.test(text.trim())) {
          addBot("Great — before we pick a time, I need to confirm a few details for your appointment.");
          addBot("What name should we use on your booking confirmation?");
          setStep("confirm_name");
        } else {
          addBot("No problem — a coordinator will reach out soon. Take care! 💙");
          setStep("done");
        }
        break;
      }
      case "confirm_name": {
        const r = validateName(text);
        if (!r.ok) { addError("Name", r.message); return; }
        setCollected(p => ({ ...p, booking_name: r.value }));
        addBot("Thank you. What email should we send the confirmation to?");
        setStep("confirm_email");
        break;
      }
      case "confirm_email": {
        const r = validateEmail(text);
        if (!r.ok) { addError("Email", r.message); return; }
        setCollected(p => ({ ...p, booking_email: r.value }));
        addBot("And what phone number should we call you at?");
        setStep("confirm_phone");
        break;
      }
      case "confirm_phone": {
        const r = validatePhone(text);
        if (!r.ok) { addError("Phone", r.message); return; }
        setCollected(p => ({ ...p, booking_phone: r.value }));
        await loadSlots();
        break;
      }
      case "select_slot":
      case "booking_confirmed":
        addBot("Thanks! Anything else? Type \"restart\" to start a new chat.");
        break;
      case "retry_name":
        setCollected(p => ({ ...p, name: text }));
        addBot("Got it, thank you. Let me try saving that again…");
        await submitLead({ ...collected, name: text }, collected.payer_source ?? "");
        break;
      case "retry_zip":
        setCollected(p => ({ ...p, zip_code: text.trim() }));
        addBot("Thanks for the corrected ZIP. Trying again…");
        await submitLead({ ...collected, zip_code: text.trim() }, collected.payer_source ?? "");
        break;
      case "retry_urgency": {
        const u = parseInt(text);
        if (!Number.isInteger(u) || u < 1 || u > 5) {
          addError("Urgency", "Please give me a single whole number between 1 and 5.");
          return;
        }
        setCollected(p => ({ ...p, urgency: u }));
        addBot("Thanks. Saving that now…");
        await submitLead({ ...collected, urgency: u }, collected.payer_source ?? "");
        break;
      }
      case "retry_hours": {
        const h = parseFloat(text);
        if (!Number.isFinite(h) || h < 0 || h > 168) {
          addError("Hours per week", "Hours per week should be a number between 0 and 168. Could you try again?");
          return;
        }
        setCollected(p => ({ ...p, hours_needed: h }));
        addBot("Perfect. Trying again…");
        await submitLead({ ...collected, hours_needed: h }, collected.payer_source ?? "");
        break;
      }
      case "retry_payer":
        setCollected(p => ({ ...p, payer_source: text }));
        addBot("Thank you. Let me save that…");
        await submitLead({ ...collected, payer_source: text }, text);
        break;
      case "done":
        if (text.toLowerCase().includes("restart") || text.toLowerCase().includes("start over")) {
          resetChat();
          return;
        }
        addBot("Thank you for chatting with us! A coordinator will be in touch. 💙");
        break;
      default:
        addBot("Thank you for chatting with us! A coordinator will be in touch. 💙");
    }
  }

  function resetChat() {
    setCollected({});
    setMessages([]);
    setStep("greeting");
    setWasIdempotent(false);
    setRetryStatus(null);
    setLeadId(null);
    setSlots([]);
    setBookingSlot(null);
    setConfirmedSlot(null);
    // New chat session => new idempotency key.
    idempotencyKeyRef.current =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? `chat-${crypto.randomUUID()}`
        : `chat-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      addBot("No problem — let's start fresh. 💙 Could you tell me the name of the person you're looking for care for?");
      setStep("name");
    }, 600);
  }

  // Maps a backend validation `field` to the step where we re-prompt.
  const RETRY_STEP_BY_FIELD: Record<string, { step: Step; prompt: string }> = {
    name: { step: "retry_name", prompt: "Could you share the name of the person needing care one more time?" },
    zip_code: { step: "retry_zip", prompt: "Could you share the 5-digit ZIP code where care is needed?" },
    urgency: { step: "retry_urgency", prompt: "On a scale of 1 to 5, how urgent is your need? (1 = exploring, 5 = immediately)" },
    hours_needed: { step: "retry_hours", prompt: "Roughly how many hours per week of care are you thinking? Any number from 0 to 168 works." },
    payer_source: { step: "retry_payer", prompt: "How are you planning to pay for care? (Private Pay, LTC Insurance, Medicaid, Medicare, VA, or Other)" },
  };

  // Detects "no response from server" failures (offline, DNS, TLS, CORS network
  // layer, aborted fetch). These are safe to retry with the same idempotency
  // key. Anything that produced an HTTP response — including 4xx validation
  // errors and 5xx — is NOT retried here, because the server may have already
  // accepted the lead, and the idempotency table will dedupe a manual retry.
  function isNetworkError(err: unknown): boolean {
    if (!err) return false;
    if (err instanceof TypeError) return true; // fetch network failure
    const name = (err as { name?: string })?.name;
    if (name === "AbortError" || name === "NetworkError") return true;
    const msg = String((err as { message?: string })?.message ?? "").toLowerCase();
    return (
      msg.includes("failed to fetch") ||
      msg.includes("network") ||
      msg.includes("load failed") ||
      msg.includes("fetch failed")
    );
  }

  // Sentinel error thrown when the user cancels retries.
  class RetryCancelledError extends Error {
    constructor() { super("Retry cancelled by user"); this.name = "RetryCancelledError"; }
  }

  async function invokeSubmitWithRetry(body: Record<string, any>, maxAttempts = 3) {
    retryAbortRef.current = { cancelled: false, timeoutId: null };
    let lastErr: unknown = null;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      if (retryAbortRef.current.cancelled) throw new RetryCancelledError();
      try {
        const result = await supabase.functions.invoke("submit-chat-lead", { body });
        // supabase-js wraps non-2xx as { data, error }; surface to caller as-is.
        // A returned `error` from a successful HTTP roundtrip is NOT a network
        // error and should not trigger a retry.
        setRetryStatus(null);
        return result;
      } catch (err) {
        lastErr = err;
        if (!isNetworkError(err) || attempt === maxAttempts) {
          setRetryStatus(null);
          throw err;
        }
        // Show "Reconnecting… (attempt N+1 of max)" before the next try.
        setRetryStatus({ attempt: attempt + 1, max: maxAttempts });
        // Exponential backoff: 500ms, 1500ms — cancellable.
        const delay = 500 * Math.pow(3, attempt - 1);
        try {
          await new Promise<void>((resolve, reject) => {
            const id = window.setTimeout(() => {
              retryAbortRef.current.timeoutId = null;
              resolve();
            }, delay);
            retryAbortRef.current.timeoutId = id;
            // If already cancelled before scheduling, reject immediately.
            if (retryAbortRef.current.cancelled) {
              window.clearTimeout(id);
              retryAbortRef.current.timeoutId = null;
              reject(new RetryCancelledError());
            }
          });
        } catch (cancelErr) {
          setRetryStatus(null);
          throw cancelErr;
        }
        if (retryAbortRef.current.cancelled) {
          setRetryStatus(null);
          throw new RetryCancelledError();
        }
      }
    }
    setRetryStatus(null);
    throw lastErr;
  }

  function cancelRetries() {
    const ref = retryAbortRef.current;
    ref.cancelled = true;
    if (ref.timeoutId !== null) {
      window.clearTimeout(ref.timeoutId);
      ref.timeoutId = null;
    }
    setRetryStatus(null);
  }

  async function submitLead(final: Record<string, any>, payerText: string) {
    if (isSubmitting) return; // Guard against double submits
    const transcript = messages.map(m => `${m.role === "assistant" ? "Sarah" : "User"}: ${m.content}`).join("\n");

    if (!user) {
      addBot("Thank you so much — I have everything I need. Someone from our team will be in touch shortly. Take care! 💙");
      setStep("done");
      return;
    }

    setIsSubmitting(true);
    setIsTyping(true);
    try {
      const { data, error } = await invokeSubmitWithRetry({
        name: final.name || "Unknown",
        zip_code: final.zip_code,
        acuity_notes: final.acuity_notes,
        urgency: final.urgency,
        hours_needed: final.hours_needed,
        payer_source: payerText || final.payer_source,
        transcript,
        idempotency_key: idempotencyKeyRef.current,
      });

      const payload = (data ?? {}) as { error?: string; field?: string; ok?: boolean; idempotent?: boolean; lead_id?: string };
      const errMsg = payload.error || error?.message;

      setIsTyping(false);

      if (!errMsg && payload.idempotent) {
        setWasIdempotent(true);
      }

      if (errMsg) {
        const field = payload.field;
        const retry = field ? RETRY_STEP_BY_FIELD[field] : undefined;

        if (retry) {
          const fieldLabel = ({
            name: "Name",
            zip_code: "ZIP code",
            urgency: "Urgency",
            hours_needed: "Hours per week",
            payer_source: "Payer source",
          } as Record<string, string>)[field!] || "Field";
          addError(fieldLabel, `${errMsg} ${retry.prompt}`);
          setStep(retry.step);
          return;
        }

        addBot(`I'm sorry — ${errMsg || "we couldn't save your information right now."} A coordinator will follow up shortly. Type "restart" if you'd like to try again.`);
        setStep("done");
        return;
      }

      if (payload.lead_id) setLeadId(payload.lead_id);
      addBot(
        wasIdempotent
          ? "Thank you — I already had everything I need from earlier. 💙 Would you like to set up a quick 15-minute call now? (yes / no)"
          : "Thank you so much, that gives me a clear picture. 💙 Would you like to set up a quick 15-minute call now to talk through next steps? (yes / no)",
      );
      setStep("offer_booking");
    } catch (err: any) {
      console.error("Failed to save lead", err);
      setIsTyping(false);
      let msg: string;
      if (err instanceof RetryCancelledError) {
        msg = "Okay, I've stopped trying. Your information wasn't saved. Type \"restart\" if you'd like to try again.";
      } else if (isNetworkError(err)) {
        msg = "I'm having trouble reaching our system — it looks like a network issue. A coordinator will follow up with you shortly. Type \"restart\" if you'd like to try again.";
      } else {
        msg = "I'm sorry — something went wrong on our end. A coordinator will follow up with you shortly. Type \"restart\" if you'd like to try again.";
      }
      addBot(msg);
      setStep("done");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function loadSlots() {
    setStep("select_slot");
    addBot("Let me check the next few openings…");
    setIsTyping(true);
    try {
      const { data, error } = await supabase.functions.invoke("get-availability", {
        body: { days: 5, max_slots: 6 },
      });
      setIsTyping(false);
      const payload = (data ?? {}) as { ok?: boolean; slots?: Slot[]; error?: string };
      if (error || !payload.ok || !payload.slots?.length) {
        addBot("I couldn't find any open slots right now — but a coordinator will reach out shortly to schedule. 💙");
        setStep("done");
        return;
      }
      setSlots(payload.slots);
      addBot("Here are the next openings — tap one that works for you:");
    } catch (e) {
      console.error("get-availability failed", e);
      setIsTyping(false);
      addBot("I'm having trouble loading available times. A coordinator will reach out to schedule with you shortly. 💙");
      setStep("done");
    }
  }

  async function handleSlotPick(slot: Slot) {
    if (bookingSlot) return;
    setBookingSlot(slot.start);
    addUser(`Book ${slot.label}`);
    setIsTyping(true);
    try {
      const { data, error } = await supabase.functions.invoke("book-appointment", {
        body: {
          lead_id: leadId,
          family_name: collected.booking_name ?? collected.name,
          family_phone: collected.booking_phone ?? null,
          family_email: collected.booking_email ?? null,
          scheduled_at: slot.start,
          duration_minutes: 15,
          notes: collected.acuity_notes ?? null,
          source: "sarah_chat",
        },
      });
      setIsTyping(false);
      const payload = (data ?? {}) as { ok?: boolean; error?: string; code?: string };
      if (error || !payload.ok) {
        if (payload.code === "slot_conflict") {
          addBot("That slot was just taken — let me refresh the openings.");
          setBookingSlot(null);
          await loadSlots();
          return;
        }
        addBot(payload.error ?? "I couldn't confirm that time. A coordinator will reach out shortly. 💙");
        setStep("done");
        return;
      }
      setConfirmedSlot(slot);
      setStep("booking_confirmed");
      addBot(
        `You're booked for ${slot.label}. We'll call ${collected.booking_phone ?? collected.family_phone ?? "you"} at that time. A confirmation is on its way to your email. 💙`,
      );
    } catch (e) {
      console.error("book-appointment failed", e);
      setIsTyping(false);
      addBot("I'm having trouble booking that time. A coordinator will reach out shortly. 💙");
      setStep("done");
    } finally {
      setBookingSlot(null);
    }
  }

  return (
    <div className={theme === "dark" ? "dark" : undefined}>
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-16 md:bottom-6 right-4 md:right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-primary shadow-lg transition-transform hover:scale-105 active:scale-95"
          aria-label="Open chat"
        >
          <MessageCircle className="h-6 w-6 text-primary-foreground" />
        </button>
      )}

      {isOpen && (
        <div className="fixed bottom-16 md:bottom-6 right-4 md:right-6 z-50 flex w-[360px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border bg-card shadow-2xl sm:w-[400px]" style={{ height: "min(560px, calc(100vh - 6rem))" }}>
          <div className="flex items-center gap-3 bg-primary px-4 py-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-foreground/20 text-sm font-bold text-primary-foreground">S</div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-primary-foreground">Sarah — Care Advisor</p>
              <p className="text-xs text-primary-foreground/70">LegacyGrowth Engine</p>
            </div>
            <button
              onClick={toggleTheme}
              className="text-primary-foreground/70 hover:text-primary-foreground focus:outline-none focus:ring-2 focus:ring-primary-foreground/40 rounded"
              aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
              aria-pressed={theme === "dark"}
              title={theme === "dark" ? "Light mode" : "Dark mode"}
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <button onClick={() => setIsOpen(false)} className="text-primary-foreground/70 hover:text-primary-foreground">
              <X className="h-5 w-5" />
            </button>
          </div>

          <p className="border-b bg-muted/50 px-3 py-2 text-[11px] leading-snug text-muted-foreground">
            HIPAA notice: Information you share is used only to coordinate care. Do not submit
            payment card or Social Security numbers in this chat.{" "}
            <a href="/hipaa" target="_blank" rel="noopener noreferrer" className="underline">
              Learn more
            </a>
          </p>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map(m => {
              if (m.role === "error") {
                return (
                  <div key={m.id} className="flex justify-start" role="alert" aria-live="polite">
                    <div className="max-w-[85%] rounded-2xl rounded-bl-md border border-destructive/30 bg-destructive/10 px-3.5 py-2.5 text-sm leading-relaxed text-destructive">
                      <div className="flex items-center gap-1.5 mb-1">
                        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                        <span className="text-[11px] font-bold uppercase tracking-wide">
                          Please fix: {m.field}
                        </span>
                      </div>
                      <p className="text-foreground/90">{m.content}</p>
                    </div>
                  </div>
                );
              }
              return (
                <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-primary text-primary-foreground rounded-br-md"
                      : "bg-secondary text-secondary-foreground rounded-bl-md"
                  }`}>
                    {m.content}
                  </div>
                </div>
              );
            })}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-secondary rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="h-2 w-2 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="h-2 w-2 rounded-full bg-muted-foreground/50 animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}
            {step === "select_slot" && slots.length > 0 && !confirmedSlot && (
              <div className="flex flex-col gap-2" role="group" aria-label="Available appointment times">
                {slots.map((slot) => {
                  const busy = bookingSlot === slot.start;
                  return (
                    <button
                      key={slot.start}
                      type="button"
                      onClick={() => handleSlotPick(slot)}
                      disabled={!!bookingSlot}
                      className="flex items-center justify-between rounded-xl border border-border bg-card px-3.5 py-2.5 text-left text-sm font-medium transition hover:border-primary hover:bg-primary/5 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <span className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-primary" />
                        {slot.label}
                      </span>
                      {busy ? (
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      ) : (
                        <span className="text-xs font-semibold text-primary">Book</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            {confirmedSlot && (
              <div className="flex justify-start" role="status" aria-live="polite">
                <div className="flex items-start gap-2 rounded-xl border border-primary/30 bg-primary/5 px-3.5 py-2.5 text-sm">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <div>
                    <p className="font-semibold text-foreground">Call confirmed</p>
                    <p className="text-xs text-muted-foreground">{confirmedSlot.label}</p>
                  </div>
                </div>
              </div>
            )}
            {retryStatus && (
              <div className="flex justify-center" role="status" aria-live="polite">
                <div className="flex items-center gap-2 rounded-full border border-border bg-muted px-3 py-1 text-[11px] font-medium text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  <span>Reconnecting… (attempt {retryStatus.attempt} of {retryStatus.max})</span>
                  <button
                    type="button"
                    onClick={cancelRetries}
                    className="ml-1 rounded-full px-2 py-0.5 text-[11px] font-semibold text-foreground hover:bg-background/70 focus:outline-none focus:ring-2 focus:ring-ring"
                    aria-label="Cancel retry attempts"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="border-t p-3">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && !isSubmitting && !isTyping) handleSend(); }}
                placeholder={
                  step === "done"
                    ? "Chat ended"
                    : isSubmitting
                      ? "Saving your information…"
                      : "Type your message..."
                }
                disabled={step === "done" || isSubmitting}
                aria-busy={isSubmitting}
                aria-invalid={inlineError ? true : undefined}
                aria-describedby={inlineError ? "chat-inline-error" : undefined}
                className={`flex-1 rounded-xl border bg-background px-4 py-2.5 text-sm outline-none focus:ring-2 disabled:opacity-50 ${
                  inlineError
                    ? "border-destructive/60 focus:ring-destructive/40"
                    : "focus:ring-ring"
                }`}
              />
              <Button
                size="icon"
                onClick={handleSend}
                disabled={step === "done" || isSubmitting || isTyping || !input.trim() || !!inlineError}
                aria-label={isSubmitting ? "Sending" : "Send message"}
                className="rounded-xl"
              >
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
            {inlineError && (
              <p
                id="chat-inline-error"
                role="status"
                aria-live="polite"
                className="mt-2 flex items-start gap-1.5 text-[11px] leading-snug text-destructive"
              >
                <AlertCircle className="mt-px h-3 w-3 shrink-0" />
                <span>{inlineError}</span>
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
